import { useEffect, useState } from "react";
import type { PdfAnnotation } from "./types";
import { createPdfAnnotation, listPdfAnnotations, deletePdfAnnotation } from "./storage";
import type { NormRect } from "../selection/types";

export function usePdfAnnotations(contentId: string | null, userId: string | null) {
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contentId) return;

    let alive = true;
    setLoading(true);

    listPdfAnnotations(contentId)
      .then((rows) => {
        if (!alive) return;
        setAnnotations(rows);
      })
      .catch((err) => console.error("Failed to load annotations:", err))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [contentId]);

  async function addAnnotation(input: {
    pageNumber: number;
    text: string;
    rects: NormRect[];
    type: PdfAnnotation["type"];
    payload: PdfAnnotation["payload"];
  }) {
    if (!contentId || !userId) return;

    // Optimistic UI
    const tempId = `tmp_${Math.random().toString(16).slice(2)}`;
    const temp: PdfAnnotation = {
      id: tempId,
      contentId,
      userId,
      pageNumber: input.pageNumber,
      text: input.text,
      rects: input.rects,
      type: input.type,
      payload: input.payload,
      createdAt: new Date().toISOString(),
    };
    setAnnotations((prev) => [...prev, temp]);

    try {
      const row = await createPdfAnnotation({
        contentId,
        userId,
        pageNumber: input.pageNumber,
        text: input.text,
        rects: input.rects,
        type: input.type,
        payload: input.payload,
      });

      // Replace temp with real
      setAnnotations((prev) =>
        prev.map((a) =>
          a.id === tempId ? { ...a, id: row.id, createdAt: row.created_at } : a
        )
      );
    } catch (e) {
      console.error("Failed to create annotation:", e);
      // Rollback
      setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
    }
  }

  async function removeAnnotation(id: string) {
    const before = annotations;
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    try {
      if (!id.startsWith("tmp_")) {
        await deletePdfAnnotation(id);
      }
    } catch (e) {
      console.error("Failed to delete annotation:", e);
      setAnnotations(before);
    }
  }

  return { annotations, loading, addAnnotation, removeAnnotation, setAnnotations };
}
